import React from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../../context/DarkModeContext';
import { CalendarDaysIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface TimelineHeaderProps {
  daysUntilEnd: number;
  isUrgent: boolean;
}

const TimelineHeader: React.FC<TimelineHeaderProps> = ({ daysUntilEnd, isUrgent }) => {
  const { darkMode } = useDarkMode();
  
  const bgColor = isUrgent 
    ? (darkMode ? 'bg-red-900/20' : 'bg-red-50')
    : (darkMode ? 'bg-blue-900/20' : 'bg-blue-50');
  
  const borderColor = isUrgent
    ? (darkMode ? 'border-red-800' : 'border-red-200')
    : (darkMode ? 'border-blue-800' : 'border-blue-200');
  
  const textColor = isUrgent
    ? (darkMode ? 'text-red-300' : 'text-red-800')
    : (darkMode ? 'text-blue-300' : 'text-blue-800');
  
  const IconComponent = isUrgent ? ExclamationTriangleIcon : CalendarDaysIcon;
  
  const getMessage = () => {
    if (daysUntilEnd <= 0) {
      return "Your internship has ended. Time to reflect on your journey!";
    } else if (daysUntilEnd === 1) {
      return "Tomorrow is your last day! Make sure you're prepared.";
    } else if (daysUntilEnd <= 3) {
      return "Final countdown! Review your talking points and confirm your evaluation meeting.";
    } else if (daysUntilEnd <= 7) {
      return "One week left! Focus on filling any evidence gaps and preparing your story.";
    } else {
      return "Your final evaluation is approaching. Let's make sure you're prepared to showcase your growth and achievements!";
    }
  };
  
  const getTimeDisplay = () => {
    if (daysUntilEnd < 0) {
      const daysAgo = Math.abs(daysUntilEnd);
      return `Completed ${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`;
    }
    return `${daysUntilEnd} ${daysUntilEnd === 1 ? 'day' : 'days'}`;
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <IconComponent className={`w-6 h-6 ${textColor}`} />
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          Timeline Status
        </h3>
      </div>
      <div className="space-y-3">
        <div className={`text-3xl font-bold ${textColor}`}>
          {getTimeDisplay()}
        </div>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {getMessage()}
        </p>
      </div>
    </div>
  );
};

export default TimelineHeader;